import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  Row,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { AsyncAutocompleteCandidate } from "components/Common/AsyncSearchSelects";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import Loaders from "components/Common/Loaders";
import MaskedIdTableCell from "components/Common/MaskedIdTableCell";
import ServerListPagination, {
  resolveServerPageSizeMeta,
} from "components/Common/ServerListPagination";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";
import { buildCandidateAsyncFilterInitialOption } from "helpers/metadataFilterHelpers";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import {
  fetchVerifyApplicationFileMetadata,
  submitVerifyApplicationFileData,
  submitVerifyApplicationFileFilterData,
} from "model/CandidateModel";

const VERIFY_APPLICATION_FILE_PAGE_NAME = "VerifyApplicationFile";

const verifyApplicationFileFilterSchema = Yup.object().shape({
  fromDate: Yup.string().trim().required("Start date is required"),
  toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
  <div className="filter-field-feedback-slot" aria-live="polite">
    {message ? (
      <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
        {message}
      </FormFeedback>
    ) : null}
  </div>
);

FilterFieldFeedback.propTypes = {
  message: PropTypes.string,
};

const DEFAULT_COLUMNS = [
  { Header: "SN", accessor: "serialNumber" },
  { Header: "Profile ID", accessor: "profileId" },
  { Header: "File", accessor: "file" },
  { Header: "Created on", accessor: "createdOn" },
  { Header: "Candidate", accessor: "candidateName" },
];

const mapColumnsFromMetadata = (metaColumns = []) => {
  if (!Array.isArray(metaColumns) || !metaColumns.length) return DEFAULT_COLUMNS;
  return metaColumns.map((col) => ({
    Header: col?.title || col?.field,
    accessor: pascalToCamel(col?.field) || col?.field,
    type: col?.type,
    asyncKind: col?.asyncKind,
  }));
};

const normalizeAccessorKey = (value = "") =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");

const isCandidateColumn = (col = {}) => {
  const accessor = normalizeAccessorKey(col?.accessor);
  const asyncKind = String(col?.asyncKind ?? "").trim().toLowerCase();
  return (
    accessor === "candidateid" ||
    accessor === "candidateclientid" ||
    accessor === "candidatename" ||
    asyncKind === "candidate"
  );
};

const isMaskedIdTableColumn = (col = {}) => {
  if (isCandidateColumn(col)) return false;
  const accessor = normalizeAccessorKey(col?.accessor);
  return accessor === "id" || accessor === "fadvorderplacefileid";
};

const mapVerifyRow = (row = {}, index = 0, pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE) => {
  const rowId = row?.fadvOrderPlaceFileId ?? row?.id ?? row?._id ?? `row-${index}`;
  return {
    ...row,
    id: rowId,
    serialNumber: row?.serialNumber ?? (pageNumber - 1) * pageSize + index + 1,
    candidateClientId: row?.candidateClientId ?? "",
    candidateClientIdLabel: row?.candidateName ?? "",
    candidateName: row?.candidateName ?? "",
    _rowKey: String(rowId),
  };
};

const buildSubmitRow = (row = {}) => ({
  fadvOrderPlaceFileId: String(row?.fadvOrderPlaceFileId ?? ""),
  candidateClientId: String(row?.candidateClientId ?? ""),
});

const hasSelectedCandidate = (row = {}) =>
  Boolean(String(row?.candidateClientId ?? "").trim());

const toFilterIsoDate = (value, endOfDay = false) => {
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return null;
  return endOfDay ? parsed.endOf("day").toISOString() : parsed.startOf("day").toISOString();
};

const normalizeVerifyApplicationFileFilterPayload = (raw = {}) => ({
  fromDate: toFilterIsoDate(raw?.fromDate, false),
  toDate: toFilterIsoDate(raw?.toDate, true),
  pageNumber: Number(raw?.pageNumber) || 1,
  pageSize: Number(raw?.pageSize) || DEFAULT_PAGE_SIZE,
});

const formatCreatedOn = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY hh:mm A") : String(value);
};

const getInlineViewUrl = (url = "") => `${String(url ?? "").trim()}/inline`;

const ApplicationFileDownloadLink = ({ href, className = "" }) => {
  const downloadUrl = String(href ?? "").trim();
  if (!downloadUrl) return null;

  return (
    <a
      href={downloadUrl}
      target="_blank"
      rel="noreferrer"
      download
      className={`client-review-download-link mt-2 ${className}`.trim()}
    >
      <i className="mdi mdi-download" aria-hidden="true" />
      <span>Download file</span>
    </a>
  );
};

ApplicationFileDownloadLink.propTypes = {
  href: PropTypes.string,
  className: PropTypes.string,
};

const ApplicationFileFullViewLink = ({ href, className = "" }) => {
  const viewUrl = getInlineViewUrl(href);
  if (!String(href ?? "").trim()) return null;

  return (
    <a
      href={viewUrl}
      target="_blank"
      rel="noreferrer"
      className={`client-review-download-link mt-2 ${className}`.trim()}
    >
      <i className="mdi mdi-open-in-new" aria-hidden="true" />
      <span>Full View</span>
    </a>
  );
};

ApplicationFileFullViewLink.propTypes = {
  href: PropTypes.string,
  className: PropTypes.string,
};

const StatusBadge = ({ value }) => {
  const text = String(value ?? "").trim();
  if (!text) return <span className="text-muted">—</span>;
  const lower = text.toLowerCase();
  const color =
    lower === "yes" || lower === "eligible" || lower.includes("pass")
      ? "success"
      : lower === "no" || lower.includes("fail")
        ? "secondary"
        : lower.includes("progress")
          ? "info"
          : "primary";
  return (
    <Badge color={color} className="candidate-csv-preview-badge">
      {text}
    </Badge>
  );
};

const renderCellValue = (col, row, { onOpenFilePreview } = {}) => {
  const accessor = col?.accessor;
  const value = row?.[accessor];

  if (value === null || value === undefined || String(value).trim() === "") {
    return <span className="text-muted">—</span>;
  }

  const normalizedAccessor = normalizeAccessorKey(accessor);

  if (isMaskedIdTableColumn(col)) {
    return <MaskedIdTableCell maskedId={String(value).trim()} />;
  }

  if (normalizedAccessor === "reportstatus") {
    return <StatusBadge value={value} />;
  }

  if (normalizedAccessor === "createdon") {
    return formatCreatedOn(value);
  }

  if (normalizedAccessor === "file") {
    const fileUrl = String(value).trim();
    const profileId = String(row?.profileId ?? "").trim();
    const previewLabel = profileId ? `Application file - ${profileId}` : "Application file";

    return (
      <button
        type="button"
        className="btn btn-link p-0 text-primary d-inline-flex align-items-center justify-content-center border-0"
        title="View file"
        aria-label="View file"
        onClick={() => onOpenFilePreview?.(fileUrl, previewLabel)}
      >
        <i className="mdi mdi-file-document-outline font-size-20" aria-hidden="true" />
      </button>
    );
  }

  return String(value);
};

const VerifyApplicationFile = () => {
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("verify-application-file");
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [filterForm, setFilterForm] = useState({
    fromDate: "",
    toDate: "",
  });
  const [filterErrors, setFilterErrors] = useState({});
  const [filterApplied, setFilterApplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tableMetaData, setTableMetadata] = useState(DEFAULT_COLUMNS);
  const [verifyRows, setVerifyRows] = useState([]);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [filePreviewModalOpen, setFilePreviewModalOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState("");
  const [previewFileAlt, setPreviewFileAlt] = useState("Application file");
  const fetchSeqRef = useRef(0);

  const openFilePreview = useCallback((fileUrl, alt = "Application file") => {
    const resolved = String(fileUrl ?? "").trim();
    if (!resolved) {
      toast.error("No file available for this row");
      return;
    }
    setPreviewFileAlt(alt);
    setPreviewFileUrl(resolved);
    setFilePreviewModalOpen(true);
  }, []);

  const closeFilePreview = useCallback(() => {
    setFilePreviewModalOpen(false);
    setPreviewFileUrl("");
    setPreviewFileAlt("Application file");
  }, []);

  const validateFilterForm = useCallback(async (form) => {
    try {
      await verifyApplicationFileFilterSchema.validate(form, { abortEarly: false });
      setFilterErrors({});
      return true;
    } catch (validationError) {
      const nextErrors = {};
      validationError?.inner?.forEach((error) => {
        nextErrors[error.path] = error.message;
      });
      setFilterErrors(nextErrors);
      return false;
    }
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      setMetadataLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:verifyApplicationFile", () =>
          fetchVerifyApplicationFileMetadata({})
        );
        const meta = metaRes?.data ?? metaRes ?? {};
        setTableMetadata(mapColumnsFromMetadata(meta?.table?.columns));
      } catch (err) {
        toast.error(err?.message || "Unable to load verify application file metadata");
        setTableMetadata(DEFAULT_COLUMNS);
      } finally {
        setMetadataLoading(false);
      }
    };
    bootstrap();
  }, [fetchMetadataSchema]);

  const loadVerifyList = useCallback(
    async ({ form = filterForm, payloadOverrides = {} } = {}) => {
      const seq = ++fetchSeqRef.current;
      setLoading(true);
      setVerifyRows([]);
      setFilterApplied(true);

      const payload = normalizeVerifyApplicationFileFilterPayload({
        ...form,
        pageNumber: payloadOverrides.pageNumber ?? requestPayload.pageNumber,
        pageSize: payloadOverrides.pageSize ?? requestPayload.pageSize,
        ...payloadOverrides,
      });

      try {
        const response = await submitVerifyApplicationFileFilterData(payload);
        if (fetchSeqRef.current !== seq) return;

        const responseData = response?.data || {};
        const listData = responseData?.records ?? responseData?.data ?? [];
        const resolvedPageSize = resolveServerPageSizeMeta(
          responseData?.pageSize,
          payload.pageSize
        );
        const resolvedPageNumber = Number(responseData?.pageNumber) || Number(payload.pageNumber) || 1;

        if (response?.success) {
          const rows = (Array.isArray(listData) ? listData : []).map((row, index) =>
            mapVerifyRow(row, index, resolvedPageNumber, resolvedPageSize)
          );
          setVerifyRows(rows);
          setRequestPayload({
            pageNumber: resolvedPageNumber,
            pageSize: resolvedPageSize,
            fromDate: form.fromDate,
            toDate: form.toDate,
          });
          setPaginationMeta({
            pageNumber: resolvedPageNumber,
            pageSize: resolvedPageSize,
            totalPages: Number(responseData?.totalPages) || 0,
            totalRecords: Number(responseData?.totalRecords) || rows.length,
          });
        } else {
          setVerifyRows([]);
          setPaginationMeta({
            pageNumber: 1,
            pageSize: resolvedPageSize,
            totalPages: 0,
            totalRecords: 0,
          });
          toast.error(response?.message || "Unable to load verify application file records.");
        }
      } catch (err) {
        if (fetchSeqRef.current !== seq) return;
        setVerifyRows([]);
        toast.error(err?.message || "Unable to load verify application file records.");
      } finally {
        if (fetchSeqRef.current === seq) setLoading(false);
      }
    },
    [filterForm, requestPayload.pageNumber, requestPayload.pageSize]
  );

  useEffect(() => {
    if (!settingsReady || metadataLoading) return;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
  }, [settingsReady, settingsPageSize, metadataLoading]);

  const handleFilterFieldChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({ ...prev, [name]: value }));
    if (filterErrors?.[name]) {
      setFilterErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSearch = async () => {
    const isValid = await validateFilterForm(filterForm);
    if (!isValid) return;
    await loadVerifyList({
      form: filterForm,
      payloadOverrides: { pageNumber: 1, pageSize: requestPayload.pageSize },
    });
  };

  const handlePageChange = async (page) => {
    const isValid = await validateFilterForm(filterForm);
    if (!isValid) return;
    await loadVerifyList({
      form: filterForm,
      payloadOverrides: { pageNumber: page, pageSize: requestPayload.pageSize },
    });
  };

  const handleRowsPerPageChange = async (rowsPerPage) => {
    const isValid = await validateFilterForm(filterForm);
    if (!isValid) return;
    await loadVerifyList({
      form: filterForm,
      payloadOverrides: { pageNumber: 1, pageSize: rowsPerPage },
    });
  };

  const handleCandidateChange = (rowKey, option) => {
    setVerifyRows((prev) =>
      prev.map((row) =>
        row._rowKey === rowKey
          ? {
              ...row,
              candidateClientId: String(option?.value ?? "").trim(),
              candidateClientIdLabel: String(option?.label ?? "").trim(),
              candidateName: String(option?.label ?? "").trim(),
            }
          : row
      )
    );
  };

  const handleCancel = () => {
    fetchSeqRef.current += 1;
    setFilterForm({ fromDate: "", toDate: "" });
    setFilterErrors({});
    setVerifyRows([]);
    setFilterApplied(false);
    setLoading(false);
    setRequestPayload((previous) => ({
      ...previous,
      pageNumber: 1,
    }));
    setPaginationMeta((previous) => ({
      ...previous,
      pageNumber: 1,
      totalPages: 0,
      totalRecords: 0,
    }));
  };

  const handleSubmit = async () => {
    const rowsToSubmit = verifyRows.filter(hasSelectedCandidate);
    if (!rowsToSubmit.length) {
      toast.error("Select at least one candidate to confirm.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        data: rowsToSubmit.map((row) => buildSubmitRow(row)),
      };
      const response = await submitVerifyApplicationFileData(payload);
      if (response?.success) {
        toast.success(response?.message || "Application file verified successfully.");
        await loadVerifyList({
          form: filterForm,
          payloadOverrides: {
            pageNumber: requestPayload.pageNumber,
            pageSize: requestPayload.pageSize,
          },
        });
      } else {
        toast.error(response?.message || "Unable to submit application file verification.");
      }
    } catch (err) {
      toast.error(err?.message || "Unable to submit application file verification.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredData = useMemo(() => verifyRows, [verifyRows]);
  const previewFileViewUrl = useMemo(
    () => getInlineViewUrl(previewFileUrl),
    [previewFileUrl]
  );
  const showTable = filterApplied && !loading && filteredData.length > 0;
  const showEmptyState =
    filterApplied && !loading && !filteredData.length && !metadataLoading;

  return (
    <div className="page-content verify-application-file-page verify-fadv-csv-page session-activity-report-page">
      <Container fluid>
        <div className="verify-fadv-csv-header mb-4">
          <div className="verify-fadv-csv-header-icon">
            <i className="mdi mdi-file-check-outline" aria-hidden />
          </div>
          <div>
            <h4 className="fw-bold mb-1">Verify Application File</h4>
            <p className="text-muted mb-0">
              Filter application file records by date range and match each row to a candidate.
            </p>
          </div>
        </div>

        <Card className="verify-fadv-csv-panel border-0 shadow-sm">
          <CardBody className="p-4">
            <LegacyFilterToolbar className="w-100">
              <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end session-activity-filter-row w-100">
                <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                  <span className="manage-user-filter-label">
                    From date <span className="text-danger">*</span>
                  </span>
                  <Input
                    type="date"
                    name="fromDate"
                    invalid={Boolean(filterErrors?.fromDate)}
                    className={`${LEGACY_FILTER_INPUT_CLASS}${
                      filterErrors?.fromDate ? " is-invalid" : ""
                    }`}
                    value={filterForm.fromDate}
                    onChange={handleFilterFieldChange}
                    disabled={loading || submitting}
                  />
                  <FilterFieldFeedback message={filterErrors?.fromDate} />
                </Col>

                <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                  <span className="manage-user-filter-label">
                    To date <span className="text-danger">*</span>
                  </span>
                  <Input
                    type="date"
                    name="toDate"
                    invalid={Boolean(filterErrors?.toDate)}
                    className={`${LEGACY_FILTER_INPUT_CLASS}${
                      filterErrors?.toDate ? " is-invalid" : ""
                    }`}
                    value={filterForm.toDate}
                    onChange={handleFilterFieldChange}
                    disabled={loading || submitting}
                  />
                  <FilterFieldFeedback message={filterErrors?.toDate} />
                </Col>

                <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                  <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                  <Button
                    color="primary"
                    className="w-100 manage-user-search-btn"
                    type="button"
                    onClick={handleSearch}
                    disabled={loading || submitting}
                  >
                    <i className="fa fa-search me-1" aria-hidden="true" />
                    Search
                  </Button>
                  <FilterFieldFeedback />
                </Col>
              </Row>
            </LegacyFilterToolbar>
          </CardBody>
        </Card>

        {metadataLoading || (loading && filterApplied) ? (
          <Card className="candidate-csv-preview-card border-0 shadow-sm mt-4">
            <CardBody className="p-5 text-center">
              <Loaders />
            </CardBody>
          </Card>
        ) : null}

        {!metadataLoading && !loading && showTable ? (
          <Card className="candidate-csv-preview-card border-0 shadow-sm mt-4">
            <CardBody className="p-4">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <h5 className="fw-bold mb-1">Application file verify records</h5>
                  <p className="text-muted mb-0 small">
                    {paginationMeta.totalRecords} record
                    {paginationMeta.totalRecords === 1 ? "" : "s"}
                  </p>
                </div>
              </div>

              <div className="table-responsive candidate-csv-preview-table-wrap">
                <Table className="candidate-csv-preview-table align-middle mb-0" hover>
                  <thead>
                    <tr>
                      {tableMetaData.map((col) => (
                        <th key={col.accessor}>{col.Header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((row) => (
                      <tr key={row._rowKey}>
                        {tableMetaData.map((col) => (
                          <td key={`${row._rowKey}-${col.accessor}`}>
                            {isCandidateColumn(col) ? (
                              <AsyncAutocompleteCandidate
                                pageName={VERIFY_APPLICATION_FILE_PAGE_NAME}
                                clientId={row?.clientId}
                                minInputLength={0}
                                emptyHint="Type at least 1 character to search candidates"
                                value={row.candidateClientId}
                                initialOption={buildCandidateAsyncFilterInitialOption(
                                  row.candidateClientId,
                                  row.candidateClientIdLabel
                                )}
                                onChange={(option) =>
                                  handleCandidateChange(row._rowKey, option)
                                }
                                placeholder="Select candidate"
                                isDisabled={submitting}
                              />
                            ) : (
                              renderCellValue(col, row, { onOpenFilePreview: openFilePreview })
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <ServerListPagination
                currentPage={paginationMeta.pageNumber}
                rowsPerPage={paginationMeta.pageSize}
                totalPages={paginationMeta.totalPages}
                totalRecords={paginationMeta.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
              />

              <div className="text-end mt-3 d-flex justify-content-end gap-2">
                <Button
                  color="light"
                  className="border"
                  type="button"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button color="primary" type="button" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Confirming..." : "Confirm"}
                </Button>
              </div>
            </CardBody>
          </Card>
        ) : null}

        {showEmptyState ? (
          <Card className="candidate-csv-preview-card border-0 shadow-sm mt-4">
            <CardBody className="p-4 text-center text-muted">No data found</CardBody>
          </Card>
        ) : null}

        <Modal
          isOpen={filePreviewModalOpen}
          toggle={closeFilePreview}
          backdrop="static"
          className="modal-full-height verify-application-file-preview-modal"
          centered
          size="xl"
        >
          <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">{previewFileAlt || "Preview File"}</h5>
              <Button close onClick={closeFilePreview} />
            </div>
            <hr />
            {previewFileViewUrl ? (
              <div className="verify-application-file-preview">
                <iframe
                  src={previewFileViewUrl}
                  title={previewFileAlt}
                  className="verify-application-file-preview__frame w-100 border"
                  style={{ minHeight: "70vh", height: "calc(95vh - 140px)" }}
                />
                <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap mt-2">
                  <ApplicationFileDownloadLink href={previewFileUrl} />
                  <ApplicationFileFullViewLink href={previewFileUrl} />
                </div>
              </div>
            ) : null}
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

VerifyApplicationFile.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(VerifyApplicationFile);
